import { useMemo } from 'react';
// Types
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
// Hooks
import { useGetFeaturedStreams } from 'hooks';

type UseStoreFeaturedParticipationParams = {
  store?: GetStoreBySlug_store;
};

type UseStoreFeaturedParticipationReturn = {
  isStoreHostInFeaturedStreams: boolean;
  isStoreParticipantInFeaturedStreams: boolean;
};

/**
 * Custom hook to check if store participates in featured streams (as participant or host)
 *
 * Returns:
 * - isStoreHostInFeaturedStreams: Whether store is host of any featured stream
 * - isStoreParticipantInFeaturedStreams: Whether store is participant in any featured stream
 */
export const useStoreFeaturedParticipation = ({
  store,
}: UseStoreFeaturedParticipationParams): UseStoreFeaturedParticipationReturn => {
  // Fetch featured streams from homepage
  const { data: featuredStreamsData } = useGetFeaturedStreams();

  // Check store's participation in featured streams (as participant or host)
  const {
    isStoreHostInFeaturedStreams,
    isStoreParticipantInFeaturedStreams,
  } = useMemo(() => {
    if (!store?.id || !featuredStreamsData?.featuredPpvEvents) {
      return {
        isStoreHostInFeaturedStreams: false,
        isStoreParticipantInFeaturedStreams: false,
      };
    }

    let isHost = false;
    let isParticipant = false;

    featuredStreamsData.featuredPpvEvents.forEach((event) => {
      const storeIsHost = event.stream?.store?.id === store.id;
      const storeIsParticipant = event.stream?.participants?.some(
        (p) => p.id === store.id
      );

      if (storeIsHost) {
        isHost = true;
      }
      if (storeIsParticipant) {
        isParticipant = true;
      }
    });

    return {
      isStoreHostInFeaturedStreams: isHost,
      isStoreParticipantInFeaturedStreams: isParticipant,
    };
  }, [store?.id, featuredStreamsData?.featuredPpvEvents]);

  return {
    isStoreHostInFeaturedStreams,
    isStoreParticipantInFeaturedStreams,
  };
};
